import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@components/Button';
import type { CreateProjectRequest } from '@services/projectService';

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProjectRequest) => Promise<void>;
}

type ModernizationIntent = 'modernise' | 'build-new' | 'db-migration' | 'tech-debt';

interface IntentOption {
  id: ModernizationIntent;
  title: string;
  description: string;
  icon: JSX.Element;
  colorClass: string;
  gradientClass: string;
}

export function NewProjectModal({ isOpen, onClose, onSubmit }: NewProjectModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedIntent, setSelectedIntent] = useState<ModernizationIntent | null>(null);
  const [formData, setFormData] = useState<CreateProjectRequest>({
    name: '',
    description: '',
    source_type: 'github',
    source_location: '',
    source_branch: 'main',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    // Reset all states when modal closes
    setStep(1);
    setSelectedIntent(null);
    setError(null);
    setFormData({
      name: '',
      description: '',
      source_type: 'github',
      source_location: '',
      source_branch: 'main',
    });
    onClose();
  };

  const handleIntentSelect = (intent: ModernizationIntent) => {
    // For 'modernise' intent, navigate to the code-graph microfrontend
    if (intent === 'modernise') {
      onClose();
      navigate('/workspace/code-graph/new');
      return;
    }

    // For 'build-new' intent, navigate to the build-software microfrontend
    if (intent === 'build-new') {
      onClose();
      navigate('/workspace/build-software');
      return;
    }

    // For 'db-migration' intent, navigate to the DB modernization microfrontend
    if (intent === 'db-migration') {
      onClose();
      navigate('/workspace/db-modernization');
      return;
    }

    // For 'tech-debt' intent, navigate to code-graph (tech debt is part of code analysis workflow)
    if (intent === 'tech-debt') {
      onClose();
      navigate('/workspace/code-graph/new?focus=tech-debt');
      return;
    }

    setSelectedIntent(intent);
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof CreateProjectRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const intentOptions: IntentOption[] = [
    {
      id: 'modernise',
      title: 'Understand an Existing Codebase',
      description: 'Reverse engineer legacy code and forward engineer modern applications',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      colorClass: 'text-blue-400',
      gradientClass: 'from-blue-500/20 to-purple-500/20',
    },
    {
      id: 'build-new',
      title: 'Build New Custom Software',
      description: 'Create custom engineered software using AI tools like Claude Code',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      colorClass: 'text-purple-400',
      gradientClass: 'from-purple-500/20 to-fuchsia-500/20',
    },
    {
      id: 'db-migration',
      title: 'Database Modernisation',
      description: 'Migrate from Oracle/MS SQL Server to PostgreSQL/MySQL',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
      colorClass: 'text-orange-400',
      gradientClass: 'from-orange-500/20 to-amber-500/20',
    },
    {
      id: 'tech-debt',
      title: 'Tech Debt Analysis & Resolution',
      description: 'Identify and resolve technical debt in your codebase',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      colorClass: 'text-green-400',
      gradientClass: 'from-green-500/20 to-emerald-500/20',
    },
  ];

  const getModalMaxWidth = () => {
    if (step === 1) return 'sm:max-w-4xl';
    return 'sm:max-w-lg';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className={`inline-block align-bottom bg-white border border-gray-200 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle ${getModalMaxWidth()} sm:w-full`}>
          {step === 1 && <IntentSelectionStep options={intentOptions} onSelect={handleIntentSelect} onClose={handleClose} />}
          {step === 2 && selectedIntent === 'modernise' && (
            <ProjectFormStep
              formData={formData}
              isSubmitting={isSubmitting}
              error={error}
              onSubmit={handleSubmit}
              onChange={handleChange}
              onBack={handleBack}
              onClose={handleClose}
            />
          )}
          {step === 2 && selectedIntent !== 'modernise' && selectedIntent && (
            <UnderConstructionStep
              intent={selectedIntent}
              intentTitle={intentOptions.find(opt => opt.id === selectedIntent)?.title || ''}
              onBack={handleBack}
              onClose={handleClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Intent Selection Step Component
interface IntentSelectionStepProps {
  options: IntentOption[];
  onSelect: (intent: ModernizationIntent) => void;
  onClose: () => void;
}

function IntentSelectionStep({ options, onSelect, onClose }: IntentSelectionStepProps) {
  return (
    <div className="px-6 pt-6 pb-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">What would you like to do?</h2>
        <p className="text-gray-600">Choose your modernization path to get started</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {options.map((option) => (
          <button
            key={option.id}
            onClick={() => onSelect(option.id)}
            className={`group relative p-6 rounded-lg border border-gray-200 bg-gradient-to-br ${option.gradientClass} backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:border-gray-300 hover:shadow-lg hover:shadow-${option.colorClass}/20 text-left`}
          >
            <div className={`${option.colorClass} mb-4 transition-transform duration-300 group-hover:scale-110`}>
              {option.icon}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{option.title}</h3>
            <p className="text-sm text-gray-600">{option.description}</p>

            {/* Hover arrow indicator */}
            <div className="absolute top-1/2 right-4 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <svg className={`w-6 h-6 ${option.colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        ))}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Project Form Step Component
interface ProjectFormStepProps {
  formData: CreateProjectRequest;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onChange: (field: keyof CreateProjectRequest, value: string) => void;
  onBack: () => void;
  onClose: () => void;
}

function ProjectFormStep({ formData, isSubmitting, error, onSubmit, onChange, onBack, onClose }: ProjectFormStepProps) {
  return (
    <form onSubmit={onSubmit}>
      <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-50 sm:mx-0 sm:h-10 sm:w-10">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </div>
          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Create New Modernization Project
            </h3>
            <div className="mt-4 space-y-4">
              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Project Name *
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => onChange('name', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border placeholder:text-gray-400"
                  placeholder="My Legacy App Modernization"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => onChange('description', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border placeholder:text-gray-400"
                  placeholder="Describe your modernization goals..."
                />
              </div>

              <div>
                <label htmlFor="source_type" className="block text-sm font-medium text-gray-700">
                  Source Type *
                </label>
                <select
                  id="source_type"
                  required
                  value={formData.source_type}
                  onChange={(e) => onChange('source_type', e.target.value as any)}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border"
                >
                  <option value="github">GitHub</option>
                  <option value="gitlab">GitLab</option>
                  <option value="bitbucket">Bitbucket</option>
                  <option value="local">Local Folder</option>
                </select>
              </div>

              <div>
                <label htmlFor="source_location" className="block text-sm font-medium text-gray-700">
                  {formData.source_type === 'local' ? 'Folder Path' : 'Repository URL'} *
                </label>
                <input
                  type="text"
                  id="source_location"
                  required
                  value={formData.source_location}
                  onChange={(e) => onChange('source_location', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border placeholder:text-gray-400"
                  placeholder={
                    formData.source_type === 'local'
                      ? '/path/to/your/project'
                      : 'https://github.com/username/repository'
                  }
                />
              </div>

              {formData.source_type !== 'local' && (
                <div>
                  <label htmlFor="source_branch" className="block text-sm font-medium text-gray-700">
                    Branch
                  </label>
                  <input
                    type="text"
                    id="source_branch"
                    value={formData.source_branch}
                    onChange={(e) => onChange('source_branch', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border placeholder:text-gray-400"
                    placeholder="main"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
          className="w-full sm:w-auto"
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creating...
            </>
          ) : (
            'Create Project'
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isSubmitting}
          className="w-full sm:w-auto mt-2 sm:mt-0"
        >
          Back
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isSubmitting}
          className="w-full sm:w-auto mt-2 sm:mt-0"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

// Under Construction Step Component
interface UnderConstructionStepProps {
  intent: ModernizationIntent;
  intentTitle: string;
  onBack: () => void;
  onClose: () => void;
}

function UnderConstructionStep({ intent, intentTitle, onBack, onClose }: UnderConstructionStepProps) {
  return (
    <div className="px-6 pt-8 pb-6">
      <div className="text-center mb-8">
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-amber-50 mb-4">
          <svg className="h-10 w-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Feature Under Construction</h3>
        <p className="text-gray-600 mb-1">This feature is coming soon!</p>
        <p className="text-gray-500 text-sm">We're working hard to bring you <span className="text-red-600 font-medium">{intentTitle}</span>.</p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
        <h4 className="text-gray-900 font-semibold mb-3">What to expect:</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Comprehensive workflow tailored to your needs</span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>AI-powered analysis and recommendations</span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Step-by-step guidance throughout the process</span>
          </li>
        </ul>
      </div>

      <div className="flex gap-2 sm:flex-row-reverse">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="w-full sm:w-auto"
        >
          Close
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="w-full sm:w-auto"
        >
          Back
        </Button>
      </div>
    </div>
  );
}
